import { createContext, useContext } from 'react';
import type React from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAnswersFromStorage } from 'packages/signup/helpers/storageHelpers';
import trackQuestionAnswered from 'packages/signup/analytics/trackQuestionAnswered';
import { WhatToUseValues, SurveyQuestionsKeys } from 'packages/signup/components/SurveyQuestions';
import type {
  NumberEmployeesSegmentationValues,
  RunPayrollValues,
  PrevPayrollProviderValues,
  IndustryIdentificationValues,
  IntentStepValues,
  WhoToPayValues,
  NumberStatesValues,
} from 'packages/signup/components/SurveyQuestions';
import type { LearnMoreSurveyQuestionsType } from 'packages/signup/components/explore/UnifiedLearnMoreJourneyQuestions';
import type { TofuSurveyQuestionsType } from 'packages/signup/components/TofuSurvey/index';
import type { SignupFormType, SubmissionOrigin } from 'packages/signup/components/signupForm/types';
import useStickyState from 'packages/signup/components/useStickyState';
import useSyncWithOldLocalStorage, {
  StorageKeys,
} from 'packages/signup/components/useSyncWithOldLocalStorage';
import useLeadSource from 'packages/signup/components/useLeadSource';

export type ContextContents = TofuSurveyQuestionsType &
  SignupFormType &
  LearnMoreSurveyQuestionsType & {
    leadType?: string;
    leadSource: string;
    tierSource: string;
    tier: string;
    currentExplorePage?: number;
    hideIntentQuestion: boolean;
    submissionOrigin?: SubmissionOrigin;
    fromBd: boolean;
    setNumberEmployeesSegmentationComplete: (answer: NumberEmployeesSegmentationValues) => void;
    setRunPayrollComplete: (answer: RunPayrollValues) => void;
    setPrevPayrollProviderComplete: (answer: PrevPayrollProviderValues) => void;
    setIndustryIdentificationComplete: (answer: IndustryIdentificationValues) => void;
    setIntentStepComplete: (answer: IntentStepValues) => void;
    setWhoToPayComplete: (answer: WhoToPayValues) => void;
    setNumberStatesComplete: (answer: NumberStatesValues) => void;
    setWhatToUseComplete: (answer: WhatToUseValues) => void;
  };

type SignupProviderProps = {
  readonly children: React.ReactNode;
  submissionOrigin?: SubmissionOrigin;
};

export const SignupContext = createContext<ContextContents>({
  setWhoToPayComplete: () => {},
  setNumberStatesComplete: () => {},
  setWhatToUseComplete: () => {},
  setNumberEmployeesSegmentationComplete: () => {},
  setRunPayrollComplete: () => {},
  setPrevPayrollProviderComplete: () => {},
  setIndustryIdentificationComplete: () => {},
  setIntentStepComplete: () => {},
  leadSource: 'Direct',
  submissionOrigin: 'Direct',
  tierSource: '',
  tier: '',
  hideIntentQuestion: false,
  fromBd: false,
});

export const SignupProvider: React.FC<SignupProviderProps> = ({
  children,
  submissionOrigin = 'Direct',
}) => {
  const oldAnswers = getAnswersFromStorage(StorageKeys.TOFU_SURVEY);
  const [searchParams] = useSearchParams();
  const hideIntentQuestion =
    searchParams.get('intent') === 'hide' ||
    ['PaidLandingPage', 'BdLandingPage', 'custompartnerlandingpage'].includes(submissionOrigin);
  const { leadSource, tierSource, tier, fromBd } = useLeadSource();
  const [numberEmployeesSegmentation, setNumberEmployeesSegmentation] = useStickyState<
    NumberEmployeesSegmentationValues | undefined
  >(oldAnswers.NUMBER_EMPLOYEES_SEGMENTATION, SurveyQuestionsKeys.NUMBER_EMPLOYEES_SEGMENTATION);

  const [runPayroll, setRunPayroll] = useStickyState<RunPayrollValues | undefined>(
    oldAnswers.RUN_PAYROLL,
    SurveyQuestionsKeys.RUN_PAYROLL
  );

  const [prevPayrollProvider, setPrevPayrollProvider] = useStickyState<
    PrevPayrollProviderValues | undefined
  >(oldAnswers.PREV_PAYROLL_PROVIDER, SurveyQuestionsKeys.PREV_PAYROLL_PROVIDER);

  const [industryIdentification, setIndustryIdentification] = useStickyState<
    IndustryIdentificationValues | undefined
  >(oldAnswers.INDUSTRY_IDENTIFICATION, SurveyQuestionsKeys.INDUSTRY_IDENTIFICATION);

  const [intentStep, setIntentStep] = useStickyState<IntentStepValues | undefined>(
    undefined,
    SurveyQuestionsKeys.INTENT_STEP
  );

  const [whoToPay, setWhoToPay] = useStickyState<WhoToPayValues | undefined>(
    undefined,
    SurveyQuestionsKeys.WHO_TO_PAY
  );

  const [numberStates, setNumberStates] = useStickyState<NumberStatesValues | undefined>(
    undefined,
    SurveyQuestionsKeys.NUMBER_STATES
  );

  const [whatToUse, setWhatToUse] = useStickyState<WhatToUseValues | string[]>(
    [WhatToUseValues.PAYROLL, WhatToUseValues.TIME_AND_ATTENDANCE],
    SurveyQuestionsKeys.WHAT_TO_USE
  );

  useSyncWithOldLocalStorage(
    numberEmployeesSegmentation,
    runPayroll,
    prevPayrollProvider,
    industryIdentification,
    intentStep,
    whoToPay,
    numberStates,
    whatToUse
  );

  const setNumberEmployeesSegmentationComplete = (answer: NumberEmployeesSegmentationValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.NUMBER_EMPLOYEES_SEGMENTATION, answer, leadSource);
    setNumberEmployeesSegmentation(answer);
  };

  const setRunPayrollComplete = (answer: RunPayrollValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.RUN_PAYROLL, answer, leadSource);
    setRunPayroll(answer);
  };

  const setPrevPayrollProviderComplete = (answer: PrevPayrollProviderValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.PREV_PAYROLL_PROVIDER, answer, leadSource);
    setPrevPayrollProvider(answer);
  };

  const setIndustryIdentificationComplete = (answer: IndustryIdentificationValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.INDUSTRY_IDENTIFICATION, answer, leadSource);
    setIndustryIdentification(answer);
  };

  const setIntentStepComplete = (answer: IntentStepValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.INTENT_STEP, answer, leadSource);
    setIntentStep(answer);
  };

  const setWhoToPayComplete = (answer: WhoToPayValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.WHO_TO_PAY, answer, leadSource);
    setWhoToPay(answer);
  };

  const setNumberStatesComplete = (answer: NumberStatesValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.NUMBER_STATES, answer, leadSource);
    setNumberStates(answer);
  };

  const setWhatToUseComplete = (answer: WhatToUseValues) => {
    trackQuestionAnswered(SurveyQuestionsKeys.WHAT_TO_USE, answer, leadSource);
    setWhatToUse(answer);
  };

  const providerValue = {
    numberEmployeesSegmentation,
    setNumberEmployeesSegmentationComplete,
    runPayroll,
    setRunPayrollComplete,
    prevPayrollProvider,
    setPrevPayrollProviderComplete,
    industryIdentification,
    setIndustryIdentificationComplete,
    intentStep,
    setIntentStepComplete,
    whoToPay,
    setWhoToPayComplete,
    numberStates,
    setNumberStatesComplete,
    whatToUse,
    setWhatToUseComplete,
    leadSource,
    tierSource,
    tier,
    hideIntentQuestion,
    submissionOrigin: fromBd ? 'BdLandingPage' : submissionOrigin,
    fromBd,
  };

  return <SignupContext.Provider value={providerValue}>{children}</SignupContext.Provider>;
};

export function useSignupContext() {
  return useContext(SignupContext);
}
